// Estado dos ajustes do simulador (imutável — cada edição devolve um novo objeto).
//
// {
//   mode:   'percentual' | 'valor'
//   nodes:  { [nodeId]: number[12] }   % por mês num nó do DRE (propaga para as categorias filhas)
//   cats:   { [codCategoria]: number[12] }  % por mês numa categoria (prevalece sobre o nó)
//   linked: { [nodeId]: boolean }      override do chip "Acompanha a receita"
//   values: { [codCategoria]: number[12] }  modo 'valor': projetado em R$ por categoria (começa zerado)
//   outside?: { [codCategoria]: true }  categorias fora da DRE vinculadas ao cenário (ver isOutsidePlanned)
// }
//
// Modo 'percentual' (pré-preenchido): Projetado = Base × (1 + %). Modo 'valor' (começar zerado):
// a projeção começa em R$ 0 e o usuário digita o valor previsto de cada linha (ver valueMode.js).
// No pré-preenchido, as células (linha × período) SEM base seguem a regra do orçamento base zero:
// recebem valor em R$ (também em `values`), que vale nos meses sem base; nos demais vale o %.
// Sempre guardado mês a mês: a quebra Trimestral grava o mesmo % nos 3 meses e a Anual nos 12.

import { MONTH_INDEXES, zeros12 } from './periods';
import { ancestorChain, primaryNodeOfCategory } from './dataset';

export const TARGET = Object.freeze({ NODE: 'no', CATEGORY: 'categoria' });

export const INPUT_MODE = Object.freeze({ PERCENT: 'percentual', VALUE: 'valor' });

export const normalizeInputMode = (mode) => (mode === INPUT_MODE.VALUE ? INPUT_MODE.VALUE : INPUT_MODE.PERCENT);

export const emptyAdjustments = (mode = INPUT_MODE.PERCENT) => ({
  mode: normalizeInputMode(mode),
  nodes: {},
  cats: {},
  linked: {},
  values: {},
});

export const isValueMode = (state) => state?.mode === INPUT_MODE.VALUE;

const bucketOf = (targetType) => (targetType === TARGET.CATEGORY ? 'cats' : 'nodes');

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Converte o texto digitado em %: aceita vírgula ou ponto, negativos e até 2 casas.
 * Caracteres inválidos são ignorados. Retorna null para vazio/inválido.
 */
export const parsePercentInput = (text) => {
  if (text == null) return null;
  if (typeof text === 'number') return Number.isFinite(text) ? round2(text) : null;
  let s = String(text).replace(/[^0-9,.-]/g, '');
  const negative = s.trim().startsWith('-');
  s = s.replace(/-/g, '');
  const lastSep = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
  if (lastSep >= 0) {
    s = `${s.slice(0, lastSep).replace(/[.,]/g, '')}.${s.slice(lastSep + 1).replace(/[.,]/g, '')}`;
  }
  if (!s || s === '.') return null;
  const value = Number(s);
  if (!Number.isFinite(value)) return null;
  return round2(negative ? -value : value);
};

export const getOwnPct = (state, targetType, key) => state?.[bucketOf(targetType)]?.[key] || null;

const isAllZero = (arr) => !arr || arr.every((v) => !v);

/** Grava `value` (%) nos meses informados de uma linha. Linha toda zerada é removida do estado. */
export const setPct = (state, targetType, key, months, value) => {
  const bucket = bucketOf(targetType);
  const current = state[bucket][key] ? [...state[bucket][key]] : zeros12();
  const v = round2(value);
  (months || MONTH_INDEXES).forEach((m) => { current[m] = v; });
  const nextBucket = { ...state[bucket] };
  if (isAllZero(current)) delete nextBucket[key];
  else nextBucket[key] = current;
  return { ...state, [bucket]: nextBucket };
};

/** Botão "=": aplica a média dos 12 meses em todos os meses. */
export const flattenToAverage = (state, targetType, key) => {
  const own = getOwnPct(state, targetType, key);
  if (!own) return state;
  const avg = own.reduce((a, b) => a + b, 0) / 12;
  return setPct(state, targetType, key, MONTH_INDEXES, avg);
};

export const clearTarget = (state, targetType, key) => {
  const bucket = bucketOf(targetType);
  if (!state[bucket][key]) return state;
  const next = { ...state[bucket] };
  delete next[key];
  return { ...state, [bucket]: next };
};

export const setLinked = (state, nodeId, value) => ({ ...state, linked: { ...state.linked, [nodeId]: Boolean(value) } });

/**
 * Resumo do % de uma linha num conjunto de meses:
 * { value, uniform, mixedKind: null | 'trimestre' | 'mes' }
 */
export const summarizePct = (pct, months = MONTH_INDEXES) => {
  if (!pct) return { value: 0, uniform: true, mixedKind: null };
  const vals = months.map((m) => Number(pct[m]) || 0);
  const uniform = vals.every((v) => v === vals[0]);
  const value = uniform ? vals[0] : round2(vals.reduce((a, b) => a + b, 0) / vals.length);
  let mixedKind = null;
  if (!uniform && months.length === 12) {
    const quarterUniform = [0, 1, 2, 3].every((q) => pct[q * 3] === pct[q * 3 + 1] && pct[q * 3] === pct[q * 3 + 2]);
    mixedKind = quarterUniform ? 'trimestre' : 'mes';
  }
  return { value, uniform, mixedKind };
};

/** % efetivo da categoria no mês: o próprio, ou o do nó ancestral mais próximo com ajuste. */
export const effectiveCategoryPct = (model, state, cod) => {
  if (state.cats[cod]) return state.cats[cod];
  const nodeId = primaryNodeOfCategory(model, cod);
  for (const id of ancestorChain(model, nodeId)) {
    if (state.nodes[id]) return state.nodes[id];
  }
  return null;
};

/** % herdado (do ancestral) — usado para mostrar o valor esmaecido na linha sem ajuste próprio. */
export const inheritedNodePct = (model, state, nodeId) => {
  const chain = ancestorChain(model, nodeId).slice(1);
  for (const id of chain) {
    if (state.nodes[id]) return state.nodes[id];
  }
  return null;
};

export const inheritedCategoryPct = (model, state, cod) => {
  const nodeId = primaryNodeOfCategory(model, cod);
  for (const id of ancestorChain(model, nodeId)) {
    if (state.nodes[id]) return state.nodes[id];
  }
  return null;
};

/** Chip "Acompanha a receita" efetivo da categoria: override do nó mais próximo ou o padrão. */
export const isCategoryLinked = (model, state, cod) => {
  if (model.revenueCats.has(cod)) return false;
  const nodeId = primaryNodeOfCategory(model, cod);
  for (const id of ancestorChain(model, nodeId)) {
    if (Object.prototype.hasOwnProperty.call(state.linked, id)) return state.linked[id];
  }
  return model.defaultLinkedCats.has(cod);
};

/** Estado do chip num nó: true/false se todas as categorias concordam; null se misto ou sem categorias. */
export const nodeLinkedState = (model, state, nodeId) => {
  const node = model.nodeById.get(nodeId);
  const cats = (node?.allCats || []).filter((c) => !model.revenueCats.has(c));
  if (!cats.length) return null;
  const first = isCategoryLinked(model, state, cats[0]);
  return cats.every((c) => isCategoryLinked(model, state, c) === first) ? first : null;
};

/** Mostra o chip só em nós de despesa/custo/dedução que têm o padrão ligado ou override. */
export const nodeShowsLinkedChip = (model, state, nodeId) => {
  const node = model.nodeById.get(nodeId);
  if (!node || node.isTotalizador) return false;
  const cats = (node.allCats || []).filter((c) => !model.revenueCats.has(c));
  if (!cats.length) return false;
  if (Object.prototype.hasOwnProperty.call(state.linked, nodeId)) return true;
  return cats.some((c) => model.defaultLinkedCats.has(c));
};

// ---------------- Categorias fora da DRE ----------------

/**
 * A categoria fora da DRE entra no orçamento do cenário? No pré-preenchido, só quando vinculada
 * (notificação "sem planejamento") ou ajustada: % próprio ou herdado da seção/categoria superior, ou
 * valor em R$ lançado. No zerado, como toda categoria, pelo valor lançado (sem valor, não grava nada).
 * O vínculo não é gravado à parte: categoria com projetado salvo no cenário volta vinculada
 * (`outsideLinksFromValues`).
 */
export const isOutsidePlanned = (model, state, cod) => {
  if (isValueMode(state)) return true;
  return Boolean(state?.outside?.[cod] || state?.values?.[cod] || effectiveCategoryPct(model, state, cod));
};

/** Categorias fora da DRE com realizado no período de referência que não estão no orçamento do cenário. */
export const unplannedOutsideCategories = (model, state) => {
  if (!model?.outsideRootId || !state || isValueMode(state)) return [];
  const hasBase = (cod) => (model.base.get(cod) || []).some((v) => Math.abs(v) > 0.005);
  return model.nodeById.get(model.outsideRootId).allCats.filter((cod) => hasBase(cod) && !isOutsidePlanned(model, state, cod));
};

/** Vincula categorias fora da DRE ao cenário: passam a entrar no orçamento com o projetado delas. */
export const linkOutsideCategories = (state, cods) => {
  if (!cods?.length) return state;
  const outside = { ...(state.outside || {}) };
  cods.forEach((cod) => { outside[cod] = true; });
  return { ...state, outside };
};

/** Vínculos de um cenário salvo: as categorias fora da DRE que têm projetado gravado (consolidado). */
export const outsideLinksFromValues = (model, values) => (Array.isArray(values) ? values : [])
  .filter((v) => !v?.codDepartamento && model?.outsideCatSet?.has(String(v?.codCategoria ?? '').trim()))
  .map((v) => String(v.codCategoria).trim());

/** Quantas linhas têm ajuste próprio (modo valor: categorias com valor lançado). */
export const countAdjusted = (state) =>
  isValueMode(state)
    ? Object.keys(state.values || {}).length
    : Object.keys(state.nodes).length + new Set([...Object.keys(state.cats), ...Object.keys(state.values || {})]).size;

// ---------------- Serialização para a API ----------------

/**
 * Para a API.
 * - Modo percentual: nós com % (levando o estado EFETIVO do chip, para a volta ser exata),
 *   categorias com %, overrides do chip em nós sem % e os valores em R$ dos meses sem base (a mesma
 *   categoria pode ir nas duas formas: % nos meses com base e R$ nos meses sem base).
 * - Modo valor: uma linha por categoria com o valor projetado mês a mês (monthlyValues).
 * O backend grava todas as linhas enviadas; linha com monthlyValues é linha de valor.
 */
export const adjustmentsToApi = (state, model) => {
  const out = [];
  Object.entries(state.values || {}).forEach(([key, values]) => {
    out.push({ targetType: TARGET.CATEGORY, targetKey: key, linkedToRevenue: false, monthlyValues: values.map(round2) });
  });
  if (isValueMode(state)) return out;
  const nodeKeys = new Set([...Object.keys(state.nodes), ...Object.keys(state.linked)]);
  nodeKeys.forEach((key) => {
    const pct = state.nodes[key];
    const hasOverride = Object.prototype.hasOwnProperty.call(state.linked, key);
    if (!pct && !hasOverride) return;
    const effective = model ? nodeLinkedState(model, state, key) : null;
    out.push({
      targetType: TARGET.NODE,
      targetKey: key,
      linkedToRevenue: hasOverride ? Boolean(state.linked[key]) : Boolean(effective),
      monthlyPct: (pct || zeros12()).map(round2),
    });
  });
  Object.entries(state.cats).forEach(([key, pct]) => {
    out.push({ targetType: TARGET.CATEGORY, targetKey: key, linkedToRevenue: false, monthlyPct: pct.map(round2) });
  });
  return out;
};

/**
 * Alinha as chaves de categoria (% e R$) à grafia do DRE da empresa, sem diferenciar maiúsculas.
 * Na Conta Azul V2/Vehro o orçamento migrado do Sync veio com o GUID em MAIÚSCULAS e o DRE usa
 * minúsculas: sem isto o simulador não reconheceria essas categorias. Havendo as duas grafias,
 * vale a do DRE.
 */
export const alignCategoryKeys = (model, state) => {
  if (!model || !state) return state;
  const canonical = new Map(model.catList.map((cod) => [String(cod).trim().toLowerCase(), cod]));
  const remap = (bucket) => {
    const entries = Object.entries(bucket || {});
    const out = {};
    const target = (key) => canonical.get(String(key).trim().toLowerCase()) || key;
    entries.forEach(([key, v]) => { if (target(key) === key) out[key] = v; });
    entries.forEach(([key, v]) => { if (target(key) !== key && !out[target(key)]) out[target(key)] = v; });
    return out;
  };
  return { ...state, cats: remap(state.cats), values: remap(state.values) };
};

const twelve = (arr) => (Array.isArray(arr) && arr.length === 12 ? arr.map((v) => Number(v) || 0) : null);

/** Reconstrói o estado a partir do cenário salvo. */
export const adjustmentsFromApi = (list, mode = INPUT_MODE.PERCENT) => {
  const state = emptyAdjustments(mode);
  (Array.isArray(list) ? list : []).forEach((adj) => {
    // Linha de valor: sempre no modo valor; no percentual, os meses sem base (orçamento base zero).
    const valueRow = isValueMode(state) || twelve(adj.monthlyValues);
    if (valueRow) {
      const values = twelve(adj.monthlyValues) || twelve(adj.monthlyPct);
      if (values && adj.targetType === TARGET.CATEGORY && values.some((v) => v)) state.values[adj.targetKey] = values;
      return;
    }
    const pct = twelve(adj.monthlyPct) || zeros12();
    const hasPct = pct.some((v) => v);
    if (adj.targetType === TARGET.CATEGORY) {
      if (hasPct) state.cats[adj.targetKey] = pct;
      return;
    }
    if (hasPct) state.nodes[adj.targetKey] = pct;
    state.linked[adj.targetKey] = Boolean(adj.linkedToRevenue);
  });
  return state;
};
